import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  BadgeDollarSign,
  Briefcase,
  Building2,
  Clipboard,
  Facebook,
  FileSpreadsheet,
  Instagram,
  KeyRound,
  Landmark,
  Mail,
  Megaphone,
  Music2,
  Plug,
  RefreshCw,
  Search,
  ShieldCheck,
  ShoppingBag,
  Slack,
  Sparkles,
  Store,
  Target,
  Unplug,
  Upload,
  Youtube,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/Dialog';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatRelative } from '@/lib/formatters';
import {
  useConnectAmazonWithToken,
  useDisconnectIntegration,
  useIntegrations,
  usePlaidItems,
  useSaveIntegrationSecret,
  useSetAmazonAppCredentials,
  useSetShopifyAppCredentials,
  useSyncPlaidItem,
  useTriggerSync,
} from '@/features/integrations/hooks';
import {
  useImportAmazonBusinessCsv,
  useRerunAmazonBusinessMatch,
  useSetAmazonBusinessAppCredentials,
} from '@/features/amazonBusiness/hooks';
import { useAmazonChannels, useShopifyChannels } from '@/features/sales/hooks';
import { startQboOAuth } from '@/features/integrations/qboOAuth';
import { startGa4OAuth } from '@/features/integrations/ga4OAuth';
import {
  getGoogleMarketingProvider,
  startGoogleMarketingOAuth,
  type GoogleMarketingProvider,
} from '@/features/integrations/googleMarketingOAuth';
import {
  shopifyRedirectUri,
  startShopifyOAuth,
} from '@/features/integrations/shopifyOAuth';
import {
  AMAZON_MARKETPLACES,
  amazonRedirectUri,
  marketplaceById,
  startAmazonOAuth,
} from '@/features/integrations/amazonOAuth';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { usePlaidConnector } from '@/features/integrations/plaidLink';
import { getMarketingProvider } from '@/features/integrations/marketingOAuth';
import { MarketingOAuthCard } from '@/pages/integrations/MarketingOAuthCard';
import type { IntegrationProvider } from '@/types/database';
import { useTestNotificationDelivery } from '@/features/notifications/hooks';

interface ProviderDef {
  id: IntegrationProvider;
  name: string;
  authType: 'api_key' | 'oauth' | 'plaid';
  syncFn?: 'mercury-sync' | 'qbo-sync' | 'plaid-sync';
  apiKeyHelp?: string;
}

const PROVIDERS: ProviderDef[] = [
  {
    id: 'mercury',
    name: 'Mercury',
    authType: 'api_key',
    syncFn: 'mercury-sync',
    apiKeyHelp:
      'Generate a read-only API token in Mercury → Settings → API. Atlas stores the token in Supabase Vault, never in plain text.',
  },
  {
    id: 'quickbooks',
    name: 'QuickBooks Online',
    authType: 'oauth',
    syncFn: 'qbo-sync',
  },
];

export function IntegrationsPage() {
  const { data: integrations, isLoading } = useIntegrations();

  return (
    <div className="space-y-8">
      <PageHeader
        title="Integrations"
      />

      {/* ── Financials & e-commerce ────────────────────────────────── */}
      <section className="space-y-3">
        <SectionHeading title="Financials & e-commerce" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {PROVIDERS.map((p) => {
            const integration = integrations?.find((i) => i.provider === p.id);
            return (
              <ProviderCard
                key={p.id}
                provider={p}
                integration={integration}
                loading={isLoading}
              />
            );
          })}

          <PlaidProviderCard
            integration={integrations?.find((i) => i.provider === 'plaid')}
            loading={isLoading}
          />

          <ShopifyProviderCard
            integration={integrations?.find((i) => i.provider === 'shopify')}
            loading={isLoading}
          />

          <AmazonProviderCard
            integration={integrations?.find((i) => i.provider === 'amazon')}
            loading={isLoading}
          />

          <AmazonBusinessProviderCard
            integration={integrations?.find((i) => i.provider === 'amazon_business')}
            loading={isLoading}
          />
        </div>
      </section>

      {/* ── Marketing — web analytics + paid ads ───────────────────── */}
      <section className="space-y-3">
        <SectionHeading
          title="Marketing — web & paid ads"
        />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Ga4ProviderCard
            integration={integrations?.find((i) => i.provider === 'ga4')}
            loading={isLoading}
          />

          <GoogleMarketingProviderCard
            provider="search_console"
            integration={integrations?.find((i) => i.provider === 'search_console')}
            loading={isLoading}
            icon={Search}
            scopeLabel="webmasters.readonly"
            resource={{ label: 'Property', metadataKey: 'site_url' }}
          />

          <MarketingOAuthCard
            provider={getMarketingProvider('amazon_ads')}
            kind="ads"
            icon={Target}
            syncFn="amazon-ads-sync"
            appDocsLabel="advertising.amazon.com/API/docs"
            appDocsHref="https://advertising.amazon.com/API/docs/en-us/setting-up/account-setup"
            appDocsHint="Register your app in the Amazon Ads developer console — separate from the SP-API app — and request the advertising::campaign_management scope."
            accountNoun={{ singular: 'profile', plural: 'profiles' }}
          />

          <MarketingOAuthCard
            provider={getMarketingProvider('google_ads')}
            kind="ads"
            icon={BadgeDollarSign}
            syncFn="google-ads-sync"
            extraCredential={{
              key: 'developer_token',
              label: 'Developer token',
              helpText:
                'From ads.google.com → Tools → API Center. Required by every Google Ads API call alongside the OAuth client.',
            }}
            appDocsLabel="Google Cloud Console + Ads API Center"
            appDocsHref="https://developers.google.com/google-ads/api/docs/oauth/cloud-project"
            appDocsHint='Create an OAuth client in Google Cloud Console, request the "https://www.googleapis.com/auth/adwords" scope, and apply for a developer token at ads.google.com/aw/apicenter.'
            accountNoun={{ singular: 'customer', plural: 'customers' }}
          />

          <MarketingOAuthCard
            provider={getMarketingProvider('meta_ads')}
            kind="ads"
            icon={Megaphone}
            syncFn="meta-ads-sync"
            appDocsLabel="developers.facebook.com"
            appDocsHref="https://developers.facebook.com/apps"
            appDocsHint="Create a Business-type app, add the Marketing API product, and request the ads_read + ads_management + business_management permissions in App Review."
            accountNoun={{ singular: 'ad account', plural: 'ad accounts' }}
          />
        </div>
      </section>

      {/* ── Marketing — organic social ─────────────────────────────── */}
      <section className="space-y-3">
        <SectionHeading
          title="Marketing — organic social"
        />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <MarketingOAuthCard
            provider={getMarketingProvider('facebook')}
            kind="social"
            icon={Facebook}
            syncFn="facebook-sync"
            appDocsLabel="developers.facebook.com"
            appDocsHref="https://developers.facebook.com/apps"
            appDocsHint="Reuse the same Facebook app as Meta Ads (or create a separate one), add Facebook Login, and request pages_show_list + pages_read_engagement + pages_read_user_content in App Review."
            accountNoun={{ singular: 'Facebook page', plural: 'Facebook pages' }}
          />

          <MarketingOAuthCard
            provider={getMarketingProvider('instagram')}
            kind="social"
            icon={Instagram}
            syncFn="instagram-sync"
            appDocsLabel="developers.facebook.com"
            appDocsHref="https://developers.facebook.com/apps"
            appDocsHint="Reuse the Facebook app, add the Instagram Graph API product, and request instagram_basic + instagram_manage_insights + pages_show_list."
            accountNoun={{ singular: 'IG business account', plural: 'IG business accounts' }}
          />

          <MarketingOAuthCard
            provider={getMarketingProvider('tiktok')}
            kind="social"
            icon={Music2}
            syncFn="tiktok-sync"
            appDocsLabel="business-api.tiktok.com"
            appDocsHref="https://business-api.tiktok.com/portal/docs?id=1738373164380162"
            appDocsHint="Create a Marketing API app, enable the Display API + Advertiser scopes, and add the Atlas callback URL to the Redirect URL list."
            accountNoun={{ singular: 'TikTok account', plural: 'TikTok accounts' }}
          />

          <GoogleMarketingProviderCard
            provider="youtube"
            integration={integrations?.find((i) => i.provider === 'youtube')}
            loading={isLoading}
            icon={Youtube}
            scopeLabel="youtube.readonly + yt-analytics.readonly"
            resource={{ label: 'Channel', metadataKey: 'channel_title' }}
          />
        </div>
      </section>

      {/* ── Notifications ──────────────────────────────────────────── */}
      <section className="space-y-3">
        <SectionHeading
          title="Notifications"
        />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <SlackProviderCard
            integration={integrations?.find((i) => i.provider === 'slack')}
            loading={isLoading}
          />
          <EmailDeliveryCard />
        </div>
      </section>
    </div>
  );
}

function SectionHeading({ title }: { title: string }) {
  return (
    <div className="space-y-0.5">
      <h2 className="text-base font-medium tracking-headline text-foreground">{title}</h2>
    </div>
  );
}

function toastDeliveryTest(
  result: {
    email: 'sent' | 'skipped' | 'error';
    slack: 'sent' | 'skipped' | 'error';
    email_error?: string;
    slack_error?: string;
  },
) {
  const bits: string[] = [];
  if (result.email === 'sent') bits.push('email sent');
  if (result.slack === 'sent') bits.push('Slack DM sent');
  if (result.email === 'error') bits.push(`email failed: ${result.email_error ?? 'unknown'}`);
  if (result.slack === 'error') bits.push(`Slack failed: ${result.slack_error ?? 'unknown'}`);
  if (result.slack === 'skipped') bits.push(`Slack skipped (${result.slack_error ?? 'not connected'})`);
  if (bits.length === 0) {
    toast.message('Test ran, nothing was sent.');
    return;
  }
  const msg = bits.join(' · ');
  if (result.email === 'error' || result.slack === 'error') toast.error(msg);
  else toast.success(msg);
}

function SlackProviderCard({
  integration,
  loading,
}: {
  integration: ReturnType<typeof useIntegrations>['data'] extends (infer T)[] | undefined
    ? T | undefined
    : never;
  loading: boolean;
}) {
  const saveSecret = useSaveIntegrationSecret();
  const disconnect = useDisconnectIntegration();
  const test = useTestNotificationDelivery();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [token, setToken] = useState('');

  const status = integration?.status ?? 'disconnected';
  const lastError = integration?.last_error;
  const isConnected = status === 'connected' || status === 'error';
  const usesChannelWebhook =
    isConnected && (integration?.metadata as { mode?: string } | null)?.mode !== 'bot_dm';

  const saveToken = async () => {
    if (!token.trim()) {
      toast.error('Bot token required');
      return;
    }
    try {
      await saveSecret.mutateAsync({ provider: 'slack', secret: token.trim() });
      setToken('');
      setDialogOpen(false);
      toast.success('Slack connected. Tagged people now get a private DM from Atlas.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save bot token.');
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect.mutateAsync('slack');
      toast.success('Slack disconnected');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Disconnect failed');
    }
  };

  const handleTest = async () => {
    try {
      const result = await test.mutateAsync();
      toastDeliveryTest(result);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Test failed');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              Slack
              <StatusBadge status={status} />
            </CardTitle>
          </div>
          <Slack className="h-5 w-5 text-muted-foreground" strokeWidth={1.6} />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" />
                Private DMs from your Slack app (token stored in Vault)
              </span>
            </div>
            {usesChannelWebhook && !lastError && (
              <p className="rounded-md bg-fresh-salmon/10 px-3 py-2 text-xs text-fresh-salmon-dark">
                Connected with the old channel webhook. Mentions are private DMs
                now, so nothing is sent until you reconnect with the app&apos;s
                bot token.
              </p>
            )}
            {lastError && (
              <p className="rounded-md bg-fresh-salmon/10 px-3 py-2 text-xs text-fresh-salmon-dark">
                {lastError}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {!isConnected ? (
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>Connect</Button>
                  </DialogTrigger>
                  <SlackTokenDialog
                    token={token}
                    setToken={setToken}
                    onSave={saveToken}
                    saving={saveSecret.isPending}
                    onClose={() => setDialogOpen(false)}
                  />
                </Dialog>
              ) : (
                <>
                  <Button onClick={handleTest} disabled={test.isPending}>
                    {test.isPending ? 'Sending…' : 'Send test'}
                  </Button>
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <KeyRound className="h-4 w-4" />
                        {usesChannelWebhook ? 'Reconnect' : 'Update token'}
                      </Button>
                    </DialogTrigger>
                    <SlackTokenDialog
                      token={token}
                      setToken={setToken}
                      onSave={saveToken}
                      saving={saveSecret.isPending}
                      onClose={() => setDialogOpen(false)}
                    />
                  </Dialog>
                  <Button
                    variant="ghost"
                    onClick={handleDisconnect}
                    disabled={disconnect.isPending}
                  >
                    <Unplug className="h-4 w-4" /> Disconnect
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SlackTokenDialog({
  token,
  setToken,
  onSave,
  saving,
  onClose,
}: {
  token: string;
  setToken: (v: string) => void;
  onSave: () => void;
  saving: boolean;
  onClose: () => void;
}) {
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Connect Slack</DialogTitle>
        <DialogDescription>
          Atlas sends each tagged person a private DM from your Slack app —
          nothing is posted in a channel. Atlas stores the token encrypted;
          rotating is submitting a new one.
        </DialogDescription>
      </DialogHeader>
      <ol className="list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
        <li>
          Open your app at{' '}
          <a
            href="https://api.slack.com/apps"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2"
          >
            api.slack.com/apps
          </a>
          , or <strong>Create New App</strong> → <em>From scratch</em> and name it{' '}
          <strong>Atlas</strong>.
        </li>
        <li>
          <em>OAuth &amp; Permissions → Scopes → Bot Token Scopes</em>: add{' '}
          <code className="rounded bg-secondary px-1">chat:write</code>,{' '}
          <code className="rounded bg-secondary px-1">users:read</code> and{' '}
          <code className="rounded bg-secondary px-1">users:read.email</code>.
        </li>
        <li>
          <em>App Home → Show Tabs</em>: turn the <strong>Messages Tab</strong> on so
          people can see Atlas&apos;s DMs.
        </li>
        <li>
          Back on <em>OAuth &amp; Permissions</em>, click{' '}
          <strong>Install</strong> (or <strong>Reinstall</strong>) <strong>to Workspace</strong>{' '}
          → <strong>Allow</strong>. Slack only applies new scopes after a reinstall.
        </li>
        <li>
          Copy the <strong>Bot User OAuth Token</strong> (
          <code className="rounded bg-secondary px-1">xoxb-…</code>) and paste it below.
          Not the app-level token (<code className="rounded bg-secondary px-1">xapp-…</code>).
        </li>
      </ol>
      <p className="text-xs text-muted-foreground">
        Atlas finds each person by their sign-in email. Anyone whose Slack email is
        different adds their Slack member ID in Settings.
      </p>
      <div className="space-y-2">
        <Label htmlFor="slack-bot-token">Bot User OAuth Token</Label>
        <Input
          id="slack-bot-token"
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="xoxb-…"
          autoComplete="off"
        />
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={onSave} disabled={saving || !token.trim()}>
          {saving ? 'Checking…' : 'Save token'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function EmailDeliveryCard() {
  const test = useTestNotificationDelivery();

  const handleTest = async () => {
    try {
      const result = await test.mutateAsync();
      toastDeliveryTest(result);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Test failed');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle>Email (Resend)</CardTitle>
          </div>
          <Mail className="h-5 w-5 text-muted-foreground" strokeWidth={1.6} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" />
              Configured via supabase secrets, not per-org
            </span>
          </div>
          <Button onClick={handleTest} disabled={test.isPending}>
            {test.isPending ? 'Sending…' : 'Send test email'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ProviderCard({
  provider,
  integration,
  loading,
}: {
  provider: ProviderDef;
  integration: ReturnType<typeof useIntegrations>['data'] extends (infer T)[] | undefined ? T | undefined : never;
  loading: boolean;
}) {
  const saveSecret = useSaveIntegrationSecret();
  const disconnect = useDisconnectIntegration();
  const sync = useTriggerSync();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [apiKey, setApiKey] = useState('');

  const status = integration?.status ?? 'disconnected';
  const lastSynced = integration?.last_synced_at;
  const lastError = integration?.last_error;
  const isConnected = status === 'connected' || status === 'syncing' || status === 'error';

  const handleStartQboOAuth = () => {
    const err = startQboOAuth();
    if (err) toast.error(err);
  };

  const saveApiKey = async () => {
    if (!apiKey.trim()) {
      toast.error('API key required');
      return;
    }
    try {
      await saveSecret.mutateAsync({ provider: provider.id, secret: apiKey.trim() });
      setApiKey('');
      setDialogOpen(false);
      toast.success(`${provider.name} connected. Pulling latest data…`);

      if (provider.syncFn) {
        try {
          await sync.mutateAsync(provider.syncFn);
          toast.success(`${provider.name} sync complete.`);
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Sync failed';
          toast.error(`Connected, but initial sync failed: ${msg}`);
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      toast.error(msg);
    }
  };

  const triggerSync = async () => {
    if (!provider.syncFn) return;
    try {
      await sync.mutateAsync(provider.syncFn);
      toast.success(`${provider.name} sync complete.`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Sync failed';
      toast.error(msg);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect.mutateAsync(provider.id);
      toast.success(`${provider.name} disconnected`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Disconnect failed';
      toast.error(msg);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              {provider.name}
              <StatusBadge status={status} />
            </CardTitle>
          </div>
          <Plug className="h-5 w-5 text-muted-foreground" strokeWidth={1.6} />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" />
                {provider.authType === 'api_key' ? 'API key (stored in Vault)' : 'OAuth 2.0'}
              </span>
              <span>Last sync: {lastSynced ? formatRelative(lastSynced) : '—'}</span>
            </div>
            {lastError && (
              <p className="rounded-md bg-fresh-salmon/10 px-3 py-2 text-xs text-fresh-salmon-dark">
                {lastError}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {!isConnected ? (
                provider.authType === 'oauth' ? (
                  <Button onClick={handleStartQboOAuth}>Connect with OAuth</Button>
                ) : (
                  <ApiKeyDialog
                    provider={provider}
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                    apiKey={apiKey}
                    setApiKey={setApiKey}
                    onSave={saveApiKey}
                    saving={saveSecret.isPending || sync.isPending}
                    triggerLabel="Connect"
                  />
                )
              ) : (
                <>
                  {provider.syncFn && (
                    <Button onClick={triggerSync} disabled={sync.isPending}>
                      <RefreshCw className={`h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`} />
                      {sync.isPending ? 'Syncing…' : 'Sync now'}
                    </Button>
                  )}
                  {provider.authType === 'api_key' ? (
                    <ApiKeyDialog
                      provider={provider}
                      open={dialogOpen}
                      onOpenChange={setDialogOpen}
                      apiKey={apiKey}
                      setApiKey={setApiKey}
                      onSave={saveApiKey}
                      saving={saveSecret.isPending || sync.isPending}
                      triggerLabel="Update key"
                      triggerVariant="outline"
                      triggerIcon={<KeyRound className="h-4 w-4" />}
                    />
                  ) : (
                    <Button variant="outline" onClick={handleStartQboOAuth}>
                      Reconnect
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={handleDisconnect}
                    disabled={disconnect.isPending}
                  >
                    <Unplug className="h-4 w-4" /> Disconnect
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Plaid is structurally different from Mercury / QBO:
 *   - One Atlas org may connect MANY Plaid Items (AMEX + Discover + Fifth
 *     Third are three separate Items, three separate access tokens).
 *   - Each Item gets its own status / last_synced / last_error in `plaid_items`.
 *   - "Connect" opens Plaid Link rather than asking for an API key, so we
 *     can't reuse the ApiKeyDialog.
 *
 * The card renders the parent integration status at the top (any-item-error
 * rolls up there) plus a per-Item table below with its own action buttons.
 */
function PlaidProviderCard({
  integration,
  loading,
}: {
  integration: ReturnType<typeof useIntegrations>['data'] extends (infer T)[] | undefined ? T | undefined : never;
  loading: boolean;
}) {
  const items = usePlaidItems();
  const sync = useTriggerSync();
  const syncOne = useSyncPlaidItem();
  const disconnect = useDisconnectIntegration();
  const linker = usePlaidConnector({
    onLinked: () => {
      // The Plaid items query will pick the new row up; the parent
      // integration row already flipped to 'connected' inside the function.
      void items.refetch();
    },
  });

  const status = integration?.status ?? 'disconnected';
  const itemsList = items.data ?? [];

  const triggerSyncAll = async () => {
    try {
      await sync.mutateAsync('plaid-sync');
      toast.success('Plaid sync complete.');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Sync failed';
      toast.error(msg);
    }
  };

  const handleDisconnectAll = async () => {
    try {
      await disconnect.mutateAsync('plaid');
      toast.success('Plaid disconnected.');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Disconnect failed';
      toast.error(msg);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              Plaid
              <StatusBadge status={status} />
            </CardTitle>
          </div>
          <Landmark className="h-5 w-5 text-muted-foreground" strokeWidth={1.6} />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={() => linker.open()}
                disabled={linker.loading}
              >
                <Plug className="h-4 w-4" />
                {linker.loading ? 'Opening Plaid…' : 'Connect a bank'}
              </Button>
              {itemsList.length > 0 && (
                <Button
                  variant="outline"
                  onClick={triggerSyncAll}
                  disabled={sync.isPending}
                >
                  <RefreshCw className={`h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`} />
                  {sync.isPending ? 'Syncing…' : 'Sync all Plaid items'}
                </Button>
              )}
              {itemsList.length > 0 && (
                <Button
                  variant="ghost"
                  onClick={handleDisconnectAll}
                  disabled={disconnect.isPending}
                >
                  <Unplug className="h-4 w-4" /> Disconnect all
                </Button>
              )}
            </div>

            {itemsList.length === 0 ? (
              <EmptyState
                icon={Building2}
                title="No banks connected via Plaid yet"
              />
            ) : (
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-left">Institution</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left">Last sync</th>
                      <th className="px-4 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsList.map((item) => (
                      <PlaidItemRow
                        key={item.id}
                        item={item}
                        onResync={() => syncOne.mutateAsync(item.plaid_item_id)}
                        syncing={syncOne.isPending}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PlaidItemRow({
  item,
  onResync,
  syncing,
}: {
  item: { id: string; plaid_item_id: string; institution_name: string | null; status: string; last_synced_at: string | null; last_error: string | null };
  onResync: () => Promise<unknown>;
  syncing: boolean;
}) {
  // Update mode: re-authenticate an item that's gone into login_required
  // without losing the access token / item id.
  const reAuth = usePlaidConnector({
    updateItemId: item.plaid_item_id,
    onLinked: () => onResync(),
  });

  const showReauth = item.status === 'login_required' || item.status === 'error';

  return (
    <tr className="border-t border-border/60">
      <td className="px-4 py-2 font-medium">
        {item.institution_name ?? <span className="text-muted-foreground">Unknown</span>}
      </td>
      <td className="px-4 py-2">
        <PlaidItemStatusBadge status={item.status} />
        {item.last_error && (
          <p className="mt-1 max-w-md text-xs text-fresh-salmon-dark" title={item.last_error}>
            {item.last_error}
          </p>
        )}
      </td>
      <td className="px-4 py-2 text-muted-foreground text-xs">
        {item.last_synced_at ? formatRelative(item.last_synced_at) : '—'}
      </td>
      <td className="px-4 py-2 text-right">
        <div className="flex justify-end gap-1.5">
          {showReauth && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => reAuth.open()}
              disabled={reAuth.loading}
            >
              {reAuth.loading ? 'Opening…' : 'Re-authenticate'}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onResync()} disabled={syncing}>
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
            Sync
          </Button>
        </div>
      </td>
    </tr>
  );
}

function ApiKeyDialog({
  provider,
  open,
  onOpenChange,
  apiKey,
  setApiKey,
  onSave,
  saving,
  triggerLabel,
  triggerVariant = 'default',
  triggerIcon,
}: {
  provider: ProviderDef;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apiKey: string;
  setApiKey: (v: string) => void;
  onSave: () => void;
  saving: boolean;
  triggerLabel: string;
  triggerVariant?: 'default' | 'outline';
  triggerIcon?: React.ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant={triggerVariant}>
          {triggerIcon}
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect {provider.name}</DialogTitle>
          {provider.apiKeyHelp && <DialogDescription>{provider.apiKeyHelp}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor={`${provider.id}-key`}>API key</Label>
          <Input
            id={`${provider.id}-key`}
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="secret_…"
            autoComplete="off"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving || !apiKey.trim()}>
            {saving ? 'Saving…' : 'Save & sync'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * GA4 connects via 3-legged Google OAuth (analytics.readonly scope). The
 * super_admin clicks "Connect with Google", consents on Google's domain,
 * and Google redirects back to /integrations/ga4/callback where
 * Ga4CallbackPage exchanges the code for a refresh token (stored in
 * integration_secrets) and prompts the user to pick which GA4 property
 * to sync. The picked property_id lands on `integrations.metadata`.
 *
 * We migrated off the original service-account JSON-key flow because
 * Google's `iam.disableServiceAccountKeyCreation` org policy now blocks
 * the JSON download. See ROADMAP §2.1 and migration 55.
 */
function Ga4ProviderCard({
  integration,
  loading,
}: {
  integration: ReturnType<typeof useIntegrations>['data'] extends (infer T)[] | undefined ? T | undefined : never;
  loading: boolean;
}) {
  const disconnect = useDisconnectIntegration();
  const sync = useTriggerSync();

  const status = integration?.status ?? 'disconnected';
  const lastSynced = integration?.last_synced_at;
  const lastError = integration?.last_error;
  const isConnected = status === 'connected' || status === 'syncing' || status === 'error';
  const existingPropertyId =
    integration?.metadata && typeof integration.metadata === 'object'
      ? ((integration.metadata as Record<string, unknown>).property_id as string | number | undefined)
      : undefined;

  const handleStartOAuth = () => {
    const err = startGa4OAuth();
    if (err) toast.error(err);
  };

  const triggerSync = async () => {
    try {
      await sync.mutateAsync('web-analytics-sync');
      toast.success('GA4 sync complete.');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Sync failed';
      toast.error(msg);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect.mutateAsync('ga4');
      toast.success('GA4 disconnected.');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Disconnect failed';
      toast.error(msg);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              Google Analytics 4
              <StatusBadge status={status} />
            </CardTitle>
          </div>
          <BarChart3 className="h-5 w-5 text-muted-foreground" strokeWidth={1.6} />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" />
                Google OAuth 2.0 (analytics.readonly)
              </span>
              <span>Last sync: {lastSynced ? formatRelative(lastSynced) : '—'}</span>
              {existingPropertyId && (
                <span>
                  Property ID: <code className="rounded bg-secondary px-1">{String(existingPropertyId)}</code>
                </span>
              )}
            </div>
            {lastError && (
              <p className="rounded-md bg-fresh-salmon/10 px-3 py-2 text-xs text-fresh-salmon-dark">
                {lastError}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {!isConnected ? (
                <Button onClick={handleStartOAuth}>Connect with Google</Button>
              ) : (
                <>
                  <Button onClick={triggerSync} disabled={sync.isPending}>
                    <RefreshCw className={`h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`} />
                    {sync.isPending ? 'Syncing…' : 'Sync now'}
                  </Button>
                  <Button variant="outline" onClick={handleStartOAuth}>
                    <KeyRound className="h-4 w-4" /> Reconnect / change property
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleDisconnect}
                    disabled={disconnect.isPending}
                  >
                    <Unplug className="h-4 w-4" /> Disconnect
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Card for the two org-scoped Google marketing integrations added with the
 * Marketing Dashboard (migration 101): Search Console and YouTube.
 *
 * They share the GA4 credential model — one Google Cloud OAuth client for all
 * three products — so unlike `MarketingOAuthCard` there's no BYO client
 * id/secret step. The operator connects, picks a property or channel on the
 * callback screen, and the hourly cron takes over.
 */
function GoogleMarketingProviderCard({
  provider,
  integration,
  loading,
  icon: Icon,
  scopeLabel,
  /** Renders the chosen resource under the status line, when one is set. */
  resource,
}: {
  provider: GoogleMarketingProvider;
  integration: ReturnType<typeof useIntegrations>['data'] extends (infer T)[] | undefined ? T | undefined : never;
  loading: boolean;
  icon: typeof BarChart3;
  scopeLabel: string;
  resource: { label: string; metadataKey: string };
}) {
  const config = getGoogleMarketingProvider(provider);
  const disconnect = useDisconnectIntegration();
  const sync = useTriggerSync();

  const status = integration?.status ?? 'disconnected';
  const lastSynced = integration?.last_synced_at;
  const lastError = integration?.last_error;
  const isConnected = status === 'connected' || status === 'syncing' || status === 'error';

  const selectedResource =
    integration?.metadata && typeof integration.metadata === 'object'
      ? ((integration.metadata as Record<string, unknown>)[resource.metadataKey] as
          | string
          | undefined)
      : undefined;

  const handleStartOAuth = () => {
    const err = startGoogleMarketingOAuth(provider);
    if (err) toast.error(err);
  };

  const triggerSync = async () => {
    try {
      await sync.mutateAsync(config.syncFunction as 'search-console-sync' | 'youtube-sync');
      toast.success(`${config.name} sync complete.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Sync failed');
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect.mutateAsync(provider);
      toast.success(`${config.name} disconnected.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Disconnect failed');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              {config.name}
              <StatusBadge status={status} />
            </CardTitle>
          </div>
          <Icon className="h-5 w-5 text-muted-foreground" strokeWidth={1.6} />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" />
                Google OAuth 2.0 ({scopeLabel})
              </span>
              <span>Last sync: {lastSynced ? formatRelative(lastSynced) : '—'}</span>
              {selectedResource && (
                <span>
                  {resource.label}:{' '}
                  <code className="rounded bg-secondary px-1">{selectedResource}</code>
                </span>
              )}
            </div>
            {lastError && (
              <p className="rounded-md bg-fresh-salmon/10 px-3 py-2 text-xs text-fresh-salmon-dark">
                {lastError}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {!isConnected ? (
                <Button onClick={handleStartOAuth}>Connect with Google</Button>
              ) : (
                <>
                  <Button onClick={triggerSync} disabled={sync.isPending}>
                    <RefreshCw className={`h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`} />
                    {sync.isPending ? 'Syncing…' : 'Sync now'}
                  </Button>
                  <Button variant="outline" onClick={handleStartOAuth}>
                    <KeyRound className="h-4 w-4" /> Reconnect / change {config.resourceLabel}
                  </Button>
                  <Button variant="ghost" onClick={handleDisconnect} disabled={disconnect.isPending}>
                    <Unplug className="h-4 w-4" /> Disconnect
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'connected') return <Badge variant="success">Connected</Badge>;
  if (status === 'syncing') return <Badge variant="info">Syncing</Badge>;
  if (status === 'error') return <Badge variant="warning">Error</Badge>;
  return <Badge variant="muted">Disconnected</Badge>;
}

function PlaidItemStatusBadge({ status }: { status: string }) {
  if (status === 'connected') return <Badge variant="success">Connected</Badge>;
  if (status === 'syncing') return <Badge variant="info">Syncing</Badge>;
  if (status === 'login_required')
    return <Badge variant="warning">Re-auth required</Badge>;
  if (status === 'error') return <Badge variant="warning">Error</Badge>;
  return <Badge variant="muted">{status}</Badge>;
}

/**
 * Shopify Admin API integration via OAuth 2.0.
 *
 * The flow has two phases, modelled as two distinct dialogs:
 *   1. **Configure app credentials** — operator pastes the Client ID +
 *      Client secret from the Shopify dev dashboard. Atlas stores the
 *      Client ID on `integrations.metadata.client_id` (non-secret) and
 *      the secret encrypted under `shopify_client_secret_<org_id>`.
 *      Until this is done, "Connect a store" is disabled.
 *   2. **Connect a store** — operator picks a `*.myshopify.com` domain;
 *      we redirect them to Shopify's `/admin/oauth/authorize` endpoint.
 *      Shopify bounces them back to /integrations/shopify/callback,
 *      which finishes the dance and provisions a `sales_channels` row.
 *
 * Why this isn't a `ProviderDef` entry: Shopify's Client ID lives per-org,
 * not per-deploy (Atlas tenants BYO Shopify app), so we can't use a
 * single `VITE_*` env var the way QBO does. The dev-dashboard model
 * also doesn't support static Admin API tokens for newer stores — OAuth
 * is the only path.
 *
 * Multiple stores: each OAuth dance creates (or updates) one
 * `sales_channels` row keyed by `config.store_domain`. Re-OAuthing the
 * same store rotates its token in place — no orphan channels.
 */
function ShopifyProviderCard({
  integration,
  loading,
}: {
  integration: ReturnType<typeof useIntegrations>['data'] extends (infer T)[] | undefined ? T | undefined : never;
  loading: boolean;
}) {
  const channels = useShopifyChannels();
  const setAppCreds = useSetShopifyAppCredentials();
  const sync = useTriggerSync();
  const disconnect = useDisconnectIntegration();
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [storeDialogOpen, setStoreDialogOpen] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [shopDomain, setShopDomain] = useState('');

  const status = integration?.status ?? 'disconnected';
  const lastSynced = integration?.last_synced_at;
  const lastError = integration?.last_error;
  const metadata = (integration?.metadata ?? {}) as Record<string, unknown>;
  const configuredClientId = (metadata.client_id as string | undefined) ?? '';
  const isAppConfigured = Boolean(configuredClientId);
  const activeChannels = (channels.data ?? []).filter((c) => c.is_active);
  const hasStores = activeChannels.length > 0;

  const handleSaveAppCreds = async () => {
    const id = clientId.trim();
    const secret = clientSecret.trim();
    if (!id || !secret) {
      toast.error('Both Client ID and Client secret are required.');
      return;
    }
    try {
      await setAppCreds.mutateAsync({ client_id: id, client_secret: secret });
      toast.success('Shopify app credentials saved. You can now connect a store.');
      setClientId('');
      setClientSecret('');
      setAppDialogOpen(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      toast.error(msg);
    }
  };

  const handleStartConnect = () => {
    if (!configuredClientId) {
      toast.error('Configure app credentials first.');
      return;
    }
    const err = startShopifyOAuth({
      clientId: configuredClientId,
      shopDomain,
    });
    if (err) toast.error(err);
    // On success the browser is already navigating to Shopify — no
    // further state to reset; sessionStorage carries the CSRF token.
  };

  const triggerSync = async () => {
    try {
      await sync.mutateAsync('shopify-sync');
      toast.success('Shopify sync complete.');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Sync failed';
      toast.error(msg);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect.mutateAsync('shopify');
      toast.success('Shopify disconnected.');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Disconnect failed';
      toast.error(msg);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              Shopify
              <StatusBadge status={status} />
            </CardTitle>
          </div>
          <ShoppingBag className="h-5 w-5 text-muted-foreground" strokeWidth={1.6} />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" />
                OAuth offline access token (encrypted per store)
              </span>
              <span>Last sync: {lastSynced ? formatRelative(lastSynced) : '—'}</span>
              {hasStores && (
                <span>
                  {activeChannels.length}{' '}
                  {activeChannels.length === 1 ? 'store' : 'stores'} connected
                </span>
              )}
            </div>

            {!isAppConfigured && (
              <p className="rounded-md border border-dashed border-border/60 bg-card/40 px-3 py-2 text-xs text-muted-foreground">
                <strong>Step 1.</strong> Configure your Shopify app's Client ID +
                Client secret (from{' '}
                <a
                  href="https://shopify.dev/dashboard"
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2"
                >
                  shopify.dev/dashboard
                </a>{' '}
                → your app → App settings). Once saved, <strong>Step 2</strong>{' '}
                connects each store via Shopify-hosted OAuth.
              </p>
            )}

            {lastError && (
              <p className="rounded-md bg-fresh-salmon/10 px-3 py-2 text-xs text-fresh-salmon-dark">
                {lastError}
              </p>
            )}

            {hasStores && (
              <ul className="space-y-1 text-sm">
                {activeChannels.map((c) => {
                  const cfg = (c.config as Record<string, unknown> | null) ?? {};
                  const domain = (cfg.store_domain as string | undefined) ?? '—';
                  return (
                    <li
                      key={c.id}
                      className="flex items-center justify-between rounded-md border border-border/60 bg-card/40 px-3 py-2"
                    >
                      <span className="font-medium">{c.name}</span>
                      <code className="text-xs text-muted-foreground">{domain}</code>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <ShopifyConfigureAppDialog
                open={appDialogOpen}
                onOpenChange={setAppDialogOpen}
                clientId={clientId}
                setClientId={setClientId}
                clientSecret={clientSecret}
                setClientSecret={setClientSecret}
                onSave={handleSaveAppCreds}
                saving={setAppCreds.isPending}
                isConfigured={isAppConfigured}
                configuredClientId={configuredClientId}
              />

              <ShopifyConnectStoreDialog
                open={storeDialogOpen}
                onOpenChange={setStoreDialogOpen}
                shopDomain={shopDomain}
                setShopDomain={setShopDomain}
                onStart={handleStartConnect}
                disabled={!isAppConfigured}
                hasStores={hasStores}
              />

              {hasStores && (
                <>
                  <Button
                    variant="outline"
                    onClick={triggerSync}
                    disabled={sync.isPending}
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`}
                    />
                    {sync.isPending ? 'Syncing…' : 'Sync now'}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleDisconnect}
                    disabled={disconnect.isPending}
                  >
                    <Unplug className="h-4 w-4" /> Disconnect
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ShopifyConfigureAppDialog({
  open,
  onOpenChange,
  clientId,
  setClientId,
  clientSecret,
  setClientSecret,
  onSave,
  saving,
  isConfigured,
  configuredClientId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  setClientId: (v: string) => void;
  clientSecret: string;
  setClientSecret: (v: string) => void;
  onSave: () => void;
  saving: boolean;
  isConfigured: boolean;
  configuredClientId: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant={isConfigured ? 'outline' : 'default'}>
          <KeyRound className="h-4 w-4" />
          {isConfigured ? 'Reconfigure app credentials' : 'Configure app credentials'}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Shopify app credentials</DialogTitle>
          <DialogDescription>
            From{' '}
            <a
              href="https://shopify.dev/dashboard"
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2"
            >
              shopify.dev/dashboard
            </a>{' '}
            → your app → <em>App settings</em>, copy the <strong>Client ID</strong>{' '}
            and reveal the <strong>Client secret</strong>. Then under{' '}
            <em>App setup → URLs</em>, add{' '}
            <code className="rounded bg-secondary px-1">{shopifyRedirectUri()}</code>{' '}
            to the <strong>Allowed redirection URL(s)</strong>. Atlas
            encrypts the secret before it touches disk; the Client ID is
            non-secret and stored alongside the integration metadata.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="shopify-client-id">Client ID</Label>
            <Input
              id="shopify-client-id"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={
                isConfigured
                  ? `Current: ${configuredClientId.slice(0, 8)}…`
                  : 'e.g. f7264459e1c61d5b46973fc666640fec'
              }
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="shopify-client-secret">Client secret</Label>
            <Input
              id="shopify-client-secret"
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={isConfigured ? '••••••••••• (re-enter to rotate)' : 'Reveal once in dev dashboard'}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !clientId.trim() || !clientSecret.trim()}
          >
            {saving ? 'Saving…' : 'Save credentials'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShopifyConnectStoreDialog({
  open,
  onOpenChange,
  shopDomain,
  setShopDomain,
  onStart,
  disabled,
  hasStores,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopDomain: string;
  setShopDomain: (v: string) => void;
  onStart: () => void;
  disabled: boolean;
  hasStores: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button disabled={disabled} variant={hasStores ? 'outline' : 'default'}>
          <Plug className="h-4 w-4" />
          {hasStores ? 'Connect another store' : 'Connect a store'}
        </Button>
      </DialogTrigger>
      <DialogContent>
          <DialogHeader>
          <DialogTitle>Connect a Shopify store</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="shopify-shop-domain">Store domain</Label>
            <Input
              id="shopify-shop-domain"
              value={shopDomain}
              onChange={(e) => setShopDomain(e.target.value)}
              placeholder="eloo-designs or eloo-designs.myshopify.com"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onStart} disabled={!shopDomain.trim()}>
            Continue to Shopify
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Amazon Seller Central SP-API integration via OAuth 2.0 (Login with Amazon).
 *
 * The flow mirrors Shopify exactly — two phases, two dialogs:
 *   1. **Configure app credentials** — operator pastes the Application ID,
 *      LWA Client ID, and LWA Client Secret from their SP-API app in
 *      Seller Central → Apps & Services → Develop Apps. The two public
 *      IDs land on `integrations.metadata`; the secret is encrypted to
 *      `amazon_client_secret_<org_id>`. Until this is done, "Connect a
 *      seller account" is disabled.
 *   2. **Connect a seller account** — operator picks the marketplace
 *      (US/EU/JP/…); we redirect them to the matching Seller Central host
 *      (`sellercentral.amazon.<tld>`). Amazon bounces them back to
 *      /integrations/amazon/callback, which finishes the dance and
 *      provisions a `sales_channels` row keyed by `selling_partner_id`.
 *
 * Why this isn't a `ProviderDef` entry: Amazon's Application ID + LWA
 * Client ID live per-org (Atlas tenants each BYO their own SP-API app)
 * so we can't use a single `VITE_*` env var the way QBO does. Atlas is
 * not published to the Selling Partner Appstore — every tenant's app
 * stays in Draft state, which the OAuth flow handles via
 * `version=beta` on the consent URL.
 *
 * Multiple seller accounts: each OAuth dance creates (or updates) one
 * `sales_channels` row keyed by `config.selling_partner_id`. Re-OAuthing
 * the same seller rotates its refresh token in place — no orphan channels.
 */
function AmazonProviderCard({
  integration,
  loading,
}: {
  integration: ReturnType<typeof useIntegrations>['data'] extends (infer T)[] | undefined ? T | undefined : never;
  loading: boolean;
}) {
  const channels = useAmazonChannels();
  const setAppCreds = useSetAmazonAppCredentials();
  const connectWithToken = useConnectAmazonWithToken();
  const sync = useTriggerSync();
  const disconnect = useDisconnectIntegration();
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [sellerDialogOpen, setSellerDialogOpen] = useState(false);
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [marketplaceId, setMarketplaceId] = useState<string>('ATVPDKIKX0DER');
  // Most BYO Atlas installs use Draft apps (they never publish to the
  // Appstore — they only authorize their own seller accounts). Default
  // `draft=true` so the consent URL includes `version=beta`. Operators
  // who do publish can untick this.
  const [draftApp, setDraftApp] = useState(true);
  // Refresh token + Merchant Token + marketplace for the self-authorization
  // paste flow. We share `marketplaceId` between the OAuth and paste paths
  // so the selected marketplace persists if the operator switches dialogs.
  // The Merchant Token (a.k.a. Seller ID, selling_partner_id) is required
  // because SP-API's marketplaceParticipations response identifies the
  // marketplaces a seller participates in but never the seller themselves —
  // Amazon only surfaces selling_partner_id on the OAuth redirect.
  const [refreshToken, setRefreshToken] = useState('');
  const [tokenSellerId, setTokenSellerId] = useState('');
  const [tokenChannelName, setTokenChannelName] = useState('');

  const status = integration?.status ?? 'disconnected';
  const lastSynced = integration?.last_synced_at;
  const lastError = integration?.last_error;
  const metadata = (integration?.metadata ?? {}) as Record<string, unknown>;
  const configuredApplicationId = (metadata.application_id as string | undefined) ?? '';
  const configuredClientId = (metadata.client_id as string | undefined) ?? '';
  const isAppConfigured = Boolean(configuredApplicationId && configuredClientId);
  const activeChannels = (channels.data ?? []).filter((c) => c.is_active);
  const hasSellers = activeChannels.length > 0;

  const handleSaveAppCreds = async () => {
    const appId = applicationId.trim();
    const id = clientId.trim();
    const secret = clientSecret.trim();
    if (!appId || !id || !secret) {
      toast.error('Application ID, Client ID, and Client secret are all required.');
      return;
    }
    try {
      await setAppCreds.mutateAsync({
        application_id: appId,
        client_id: id,
        client_secret: secret,
      });
      toast.success('Amazon app credentials saved. You can now connect a seller account.');
      setApplicationId('');
      setClientId('');
      setClientSecret('');
      setAppDialogOpen(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      toast.error(msg);
    }
  };

  const handleStartConnect = () => {
    if (!configuredApplicationId) {
      toast.error('Configure app credentials first.');
      return;
    }
    const err = startAmazonOAuth({
      applicationId: configuredApplicationId,
      marketplaceId,
      draft: draftApp,
    });
    if (err) toast.error(err);
    // On success the browser is already navigating to Seller Central — no
    // further state to reset; sessionStorage carries the CSRF token.
  };

  const handleConnectWithToken = async () => {
    const token = refreshToken.trim();
    const sellerId = tokenSellerId.trim();
    if (!token) {
      toast.error('Paste the refresh token from Seller Central.');
      return;
    }
    if (!sellerId) {
      toast.error('Paste your Merchant Token from Seller Central → Settings → Account Info.');
      return;
    }
    if (!isAppConfigured) {
      toast.error('Configure app credentials first.');
      return;
    }
    try {
      const result = await connectWithToken.mutateAsync({
        refresh_token: token,
        seller_id: sellerId,
        marketplace_id: marketplaceId,
        channel_name: tokenChannelName.trim() || undefined,
      });
      toast.success(`Connected ${result.channel_name}. Pulling orders…`);
      setRefreshToken('');
      setTokenSellerId('');
      setTokenChannelName('');
      setTokenDialogOpen(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Connection failed';
      toast.error(msg);
    }
  };

  const triggerSync = async () => {
    try {
      await sync.mutateAsync('amazon-sync');
      toast.success('Amazon sync complete.');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Sync failed';
      toast.error(msg);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect.mutateAsync('amazon');
      toast.success('Amazon disconnected.');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Disconnect failed';
      toast.error(msg);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              Amazon
              <StatusBadge status={status} />
            </CardTitle>
          </div>
          <Store className="h-5 w-5 text-muted-foreground" strokeWidth={1.6} />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" />
                LWA OAuth refresh token (encrypted per seller account)
              </span>
              <span>Last sync: {lastSynced ? formatRelative(lastSynced) : '—'}</span>
              {hasSellers && (
                <span>
                  {activeChannels.length}{' '}
                  {activeChannels.length === 1 ? 'seller' : 'sellers'} connected
                </span>
              )}
            </div>

            {!isAppConfigured && (
              <p className="rounded-md border border-dashed border-border/60 bg-card/40 px-3 py-2 text-xs text-muted-foreground">
                <strong>Step 1.</strong> Create an SP-API app in{' '}
                <a
                  href="https://sellercentral.amazon.com/sellingpartner/developerconsole"
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2"
                >
                  Seller Central → Apps & Services → Develop Apps
                </a>{' '}
                and copy its Application ID + LWA credentials.{' '}
                <strong>Step 2</strong> connects each seller account — use{' '}
                <em>Paste refresh token</em> (recommended; copy from the
                app's <em>Authorization tokens</em> panel) or the full OAuth
                flow (requires registering{' '}
                <code className="rounded bg-secondary px-1">{amazonRedirectUri()}</code>{' '}
                under <em>Login with Amazon → Allowed Return URLs</em>).
              </p>
            )}

            {lastError && (
              <p className="rounded-md bg-fresh-salmon/10 px-3 py-2 text-xs text-fresh-salmon-dark">
                {lastError}
              </p>
            )}

            {hasSellers && (
              <ul className="space-y-1 text-sm">
                {activeChannels.map((c) => {
                  const cfg = (c.config as Record<string, unknown> | null) ?? {};
                  const spId = (cfg.selling_partner_id as string | undefined) ?? '—';
                  const mp = (cfg.marketplace_id as string | undefined) ?? '';
                  const country = marketplaceById(mp)?.country ?? mp ?? '—';
                  return (
                    <li
                      key={c.id}
                      className="flex items-center justify-between rounded-md border border-border/60 bg-card/40 px-3 py-2"
                    >
                      <span className="font-medium">{c.name}</span>
                      <code className="text-xs text-muted-foreground">
                        {country} · {spId}
                      </code>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <AmazonConfigureAppDialog
                open={appDialogOpen}
                onOpenChange={setAppDialogOpen}
                applicationId={applicationId}
                setApplicationId={setApplicationId}
                clientId={clientId}
                setClientId={setClientId}
                clientSecret={clientSecret}
                setClientSecret={setClientSecret}
                onSave={handleSaveAppCreds}
                saving={setAppCreds.isPending}
                isConfigured={isAppConfigured}
                configuredApplicationId={configuredApplicationId}
                configuredClientId={configuredClientId}
              />

              <AmazonConnectSellerDialog
                open={sellerDialogOpen}
                onOpenChange={setSellerDialogOpen}
                marketplaceId={marketplaceId}
                setMarketplaceId={setMarketplaceId}
                draftApp={draftApp}
                setDraftApp={setDraftApp}
                onStart={handleStartConnect}
                disabled={!isAppConfigured}
                hasSellers={hasSellers}
              />

              <AmazonPasteTokenDialog
                open={tokenDialogOpen}
                onOpenChange={setTokenDialogOpen}
                refreshToken={refreshToken}
                setRefreshToken={setRefreshToken}
                sellerId={tokenSellerId}
                setSellerId={setTokenSellerId}
                marketplaceId={marketplaceId}
                setMarketplaceId={setMarketplaceId}
                channelName={tokenChannelName}
                setChannelName={setTokenChannelName}
                onSubmit={handleConnectWithToken}
                submitting={connectWithToken.isPending}
                disabled={!isAppConfigured}
              />

              {hasSellers && (
                <>
                  <Button
                    variant="outline"
                    onClick={triggerSync}
                    disabled={sync.isPending}
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`}
                    />
                    {sync.isPending ? 'Syncing…' : 'Sync now'}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleDisconnect}
                    disabled={disconnect.isPending}
                  >
                    <Unplug className="h-4 w-4" /> Disconnect
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AmazonConfigureAppDialog({
  open,
  onOpenChange,
  applicationId,
  setApplicationId,
  clientId,
  setClientId,
  clientSecret,
  setClientSecret,
  onSave,
  saving,
  isConfigured,
  configuredApplicationId,
  configuredClientId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId: string;
  setApplicationId: (v: string) => void;
  clientId: string;
  setClientId: (v: string) => void;
  clientSecret: string;
  setClientSecret: (v: string) => void;
  onSave: () => void;
  saving: boolean;
  isConfigured: boolean;
  configuredApplicationId: string;
  configuredClientId: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant={isConfigured ? 'outline' : 'default'}>
          <KeyRound className="h-4 w-4" />
          {isConfigured ? 'Reconfigure app credentials' : 'Configure app credentials'}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Amazon SP-API app credentials</DialogTitle>
          <DialogDescription>
            From{' '}
            <a
              href="https://sellercentral.amazon.com/sellingpartner/developerconsole"
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2"
            >
              Seller Central → Apps & Services → Develop Apps
            </a>
            , copy your <strong>App ID</strong> (top of the app detail page)
            and reveal the <strong>LWA credentials</strong> (Client ID + Client
            secret). Then under <em>Login with Amazon → Allowed Return URLs</em>
            , register{' '}
            <code className="rounded bg-secondary px-1">{amazonRedirectUri()}</code>{' '}
            — Amazon does an exact string match. Atlas encrypts the secret
            before it touches disk; the App ID + Client ID are non-secret and
            stored alongside the integration metadata.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="amazon-application-id">Application ID</Label>
            <Input
              id="amazon-application-id"
              value={applicationId}
              onChange={(e) => setApplicationId(e.target.value)}
              placeholder={
                isConfigured
                  ? `Current: ${configuredApplicationId.slice(0, 28)}…`
                  : 'amzn1.sp.solution.…'
              }
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="amazon-client-id">LWA Client ID</Label>
            <Input
              id="amazon-client-id"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={
                isConfigured
                  ? `Current: ${configuredClientId.slice(0, 32)}…`
                  : 'amzn1.application-oa2-client.…'
              }
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="amazon-client-secret">LWA Client secret</Label>
            <Input
              id="amazon-client-secret"
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={
                isConfigured ? '••••••••••• (re-enter to rotate)' : 'amzn1.oa2-cs.v1.…'
              }
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={
              saving ||
              !applicationId.trim() ||
              !clientId.trim() ||
              !clientSecret.trim()
            }
          >
            {saving ? 'Saving…' : 'Save credentials'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AmazonConnectSellerDialog({
  open,
  onOpenChange,
  marketplaceId,
  setMarketplaceId,
  draftApp,
  setDraftApp,
  onStart,
  disabled,
  hasSellers,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  marketplaceId: string;
  setMarketplaceId: (v: string) => void;
  draftApp: boolean;
  setDraftApp: (v: boolean) => void;
  onStart: () => void;
  disabled: boolean;
  hasSellers: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button disabled={disabled} variant={hasSellers ? 'outline' : 'default'}>
          <Plug className="h-4 w-4" />
          {hasSellers ? 'Connect another seller' : 'Connect a seller account'}
        </Button>
      </DialogTrigger>
      <DialogContent>
          <DialogHeader>
          <DialogTitle>Connect an Amazon seller account</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="amazon-marketplace">Marketplace</Label>
            <Select value={marketplaceId} onValueChange={setMarketplaceId}>
              <SelectTrigger id="amazon-marketplace" className="w-full">
                <SelectValue placeholder="Pick a marketplace" />
              </SelectTrigger>
              <SelectContent>
                {AMAZON_MARKETPLACES.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <label className="flex items-start gap-2 rounded-md border border-border/60 bg-card/40 px-3 py-2 text-xs">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={draftApp}
              onChange={(e) => setDraftApp(e.target.checked)}
            />
            <span className="font-medium text-foreground">My app is in Draft</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onStart} disabled={!marketplaceId}>
            Continue to Seller Central
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Self-authorization paste flow — for the common case of "I own both the
 * SP-API app and the seller account I'm authorizing".
 *
 * In Seller Central → Apps & Services → Develop Apps → your app, the
 * "Authorization tokens" panel includes a button that mints a long-lived
 * refresh token tied to the operator's own seller account. Pasting it
 * here skips Amazon's OAuth redirect dance entirely — the Edge Function
 * exchanges it for an access token, hits `/sellers/v1/marketplaceParticipations`
 * to auto-detect the selling_partner_id, and provisions the channel.
 *
 * Why this exists alongside the OAuth dialog: the new Solution Provider
 * Portal hides the LWA Security Profile's "Allowed Return URLs" field
 * behind a separate developer.amazon.com console, which trips up most
 * operators on first connect. Self-auth has no redirect, so no
 * registration step.
 */
function AmazonPasteTokenDialog({
  open,
  onOpenChange,
  refreshToken,
  setRefreshToken,
  sellerId,
  setSellerId,
  marketplaceId,
  setMarketplaceId,
  channelName,
  setChannelName,
  onSubmit,
  submitting,
  disabled,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  refreshToken: string;
  setRefreshToken: (v: string) => void;
  sellerId: string;
  setSellerId: (v: string) => void;
  marketplaceId: string;
  setMarketplaceId: (v: string) => void;
  channelName: string;
  setChannelName: (v: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  disabled: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button disabled={disabled} variant="outline">
          <Clipboard className="h-4 w-4" />
          Paste refresh token
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect with a refresh token</DialogTitle>
          <DialogDescription>
            For when you own both the SP-API app and the seller account.
            In{' '}
            <a
              href="https://sellercentral.amazon.com/sellingpartner/developerconsole"
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2"
            >
              Seller Central → Apps & Services → Develop Apps
            </a>{' '}
            → your app, open the <strong>Authorization tokens</strong>{' '}
            panel and click <em>Authorize</em>. Amazon mints a refresh token
            starting with <code className="rounded bg-secondary px-1">Atzr|…</code>
            ; paste it below. Atlas confirms the token works against
            SP-API before saving, so a bad paste fails fast instead of
            silently producing zero-order syncs.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="amazon-refresh-token">Refresh token</Label>
            <Input
              id="amazon-refresh-token"
              type="password"
              value={refreshToken}
              onChange={(e) => setRefreshToken(e.target.value)}
              placeholder="Atzr|…"
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="amazon-paste-seller-id">Merchant Token (Seller ID)</Label>
            <Input
              id="amazon-paste-seller-id"
              value={sellerId}
              onChange={(e) => setSellerId(e.target.value.toUpperCase())}
              placeholder="A1B2C3D4EXAMPLE"
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="amazon-paste-marketplace">Primary marketplace</Label>
            <Select value={marketplaceId} onValueChange={setMarketplaceId}>
              <SelectTrigger id="amazon-paste-marketplace" className="w-full">
                <SelectValue placeholder="Pick a marketplace" />
              </SelectTrigger>
              <SelectContent>
                {AMAZON_MARKETPLACES.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="amazon-paste-channel-name">
              Channel name <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="amazon-paste-channel-name"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              placeholder="Defaults to your Seller Central store name"
              autoComplete="off"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={
              submitting ||
              !refreshToken.trim() ||
              !sellerId.trim() ||
              !marketplaceId
            }
          >
            {submitting ? 'Verifying…' : 'Connect seller account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Amazon Business buyer integration (ROADMAP §1.7, migration 61).
 *
 * Completely separate from the Amazon (seller) card above — this one
 * brings *purchases from* Amazon Business into Atlas so they can be
 * line-item-split when pushed to QBO as Purchase records. It's the
 * Atlas replacement for the QBO ↔ Amazon Business integration the
 * operator just turned off.
 *
 * Dual ingestion paths exposed:
 *   1. **CSV upload** — works today, no Amazon approval needed.
 *      Operator downloads an Order History Report By Line Item
 *      (Business Settings → Business Analytics → Reports), drops it
 *      here, Atlas parses + matches against Plaid charges.
 *   2. **Scheduled API pull** — wired but inert until Amazon approves
 *      the per-org Solution Provider app (2–4 weeks typical). Once
 *      configured, the nightly `amazon-business-sync` cron handles it.
 *
 * Both paths land in the same `amazon_business_orders` table and use
 * the same matcher / multi-line QBO push pipeline.
 */
function AmazonBusinessProviderCard({
  integration,
  loading,
}: {
  integration: ReturnType<typeof useIntegrations>['data'] extends (infer T)[] | undefined
    ? T | undefined
    : never;
  loading: boolean;
}) {
  const importCsv = useImportAmazonBusinessCsv();
  const rerunMatch = useRerunAmazonBusinessMatch();
  const setAppCreds = useSetAmazonBusinessAppCredentials();
  const sync = useTriggerSync();
  const disconnect = useDisconnectIntegration();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const status = integration?.status ?? 'disconnected';
  const lastSynced = integration?.last_synced_at;
  const lastError = integration?.last_error;
  const metadata = (integration?.metadata ?? {}) as Record<string, unknown>;
  const configuredApplicationId = (metadata.application_id as string | undefined) ?? '';
  const configuredClientId = (metadata.client_id as string | undefined) ?? '';
  const isAppConfigured = Boolean(configuredApplicationId && configuredClientId);

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so the same file can be re-uploaded after a failure
    // without the operator having to refresh the page.
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Pick a .csv Order History Report.');
      return;
    }
    try {
      const result = await importCsv.mutateAsync(file);
      const match = result.match;
      const matchBits = match
        ? ` · ${match.auto_matched} auto-matched / ${match.review_required} need review / ${match.no_charge_yet} awaiting charge`
        : '';
      toast.success(
        `Imported ${result.imported_orders} order${result.imported_orders === 1 ? '' : 's'} ` +
          `(${result.imported_lines} line${result.imported_lines === 1 ? '' : 's'})${matchBits}.`,
      );
      if (result.unmatched_headers.length > 0) {
        console.warn(
          'amazon-business-import-csv: ignored unrecognised CSV columns',
          result.unmatched_headers,
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Import failed';
      toast.error(msg);
    }
  };

  const handleRerunMatch = async () => {
    try {
      const res = await rerunMatch.mutateAsync();
      const s = res.summary;
      toast.success(
        `Matcher run: ${s.auto_matched} auto-matched, ${s.review_required} need review, ` +
          `${s.no_charge_yet} awaiting charge.`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Matcher failed');
    }
  };

  const handleSaveAppCreds = async () => {
    const appId = applicationId.trim();
    const id = clientId.trim();
    const secret = clientSecret.trim();
    if (!appId || !id || !secret) {
      toast.error('Application ID, Client ID, and Client secret are all required.');
      return;
    }
    try {
      await setAppCreds.mutateAsync({
        application_id: appId,
        client_id: id,
        client_secret: secret,
      });
      toast.success(
        'Amazon Business app credentials saved. The scheduled API pull will go live ' +
          'once Amazon approves your Solution Provider app.',
      );
      setApplicationId('');
      setClientId('');
      setClientSecret('');
      setAppDialogOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Save failed');
    }
  };

  const triggerApiSync = async () => {
    try {
      await sync.mutateAsync('amazon-business-sync');
      toast.success('Amazon Business sync attempted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sync failed');
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect.mutateAsync('amazon_business');
      toast.success('Amazon Business disconnected.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Disconnect failed');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              Amazon Business
              <StatusBadge status={status} />
            </CardTitle>
          </div>
          <Briefcase className="h-5 w-5 text-muted-foreground" strokeWidth={1.6} />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" />
                CSV upload (today) · Reports API (when approved)
              </span>
              <span>Last sync: {lastSynced ? formatRelative(lastSynced) : '—'}</span>
            </div>

            {lastError && (
              <p className="rounded-md bg-fresh-salmon/10 px-3 py-2 text-xs text-fresh-salmon-dark">
                {lastError}
              </p>
            )}

            {/* Path A — CSV upload (recommended; works right now). */}
            <div className="space-y-2 rounded-md border border-border/60 bg-card/40 px-3 py-3">
              <p className="text-xs text-muted-foreground">
                <strong className="text-foreground">Upload a CSV</strong> from{' '}
                <a
                  href="https://www.amazon.com/b2b/aba/reports"
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2"
                >
                  Business Settings → Business Analytics → Reports
                </a>
                . Pick the <em>Reconciliation</em> or <em>Items</em> report type
                and date range, then drop the .csv here.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <Button onClick={handlePickFile} disabled={importCsv.isPending}>
                  <Upload className="h-4 w-4" />
                  {importCsv.isPending ? 'Importing…' : 'Upload CSV'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleRerunMatch}
                  disabled={rerunMatch.isPending}
                >
                  <Sparkles
                    className={`h-4 w-4 ${rerunMatch.isPending ? 'animate-spin' : ''}`}
                  />
                  {rerunMatch.isPending ? 'Matching…' : 'Re-run matcher'}
                </Button>
                <Button variant="ghost" asChild>
                  <Link to="/financials/amazon-business">
                    <FileSpreadsheet className="h-4 w-4" />
                    View orders
                  </Link>
                </Button>
              </div>
            </div>

            {/* Path B — scheduled API (after Amazon approval). */}
            <div className="space-y-2 rounded-md border border-dashed border-border/60 bg-card/40 px-3 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <AmazonBusinessConfigureAppDialog
                  open={appDialogOpen}
                  onOpenChange={setAppDialogOpen}
                  applicationId={applicationId}
                  setApplicationId={setApplicationId}
                  clientId={clientId}
                  setClientId={setClientId}
                  clientSecret={clientSecret}
                  setClientSecret={setClientSecret}
                  onSave={handleSaveAppCreds}
                  saving={setAppCreds.isPending}
                  isConfigured={isAppConfigured}
                  configuredApplicationId={configuredApplicationId}
                  configuredClientId={configuredClientId}
                />
                {isAppConfigured && (
                  <Button
                    variant="outline"
                    onClick={triggerApiSync}
                    disabled={sync.isPending}
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`}
                    />
                    {sync.isPending ? 'Syncing…' : 'Sync now (API)'}
                  </Button>
                )}
                {(isAppConfigured || status !== 'disconnected') && (
                  <Button
                    variant="ghost"
                    onClick={handleDisconnect}
                    disabled={disconnect.isPending}
                  >
                    <Unplug className="h-4 w-4" /> Disconnect
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AmazonBusinessConfigureAppDialog({
  open,
  onOpenChange,
  applicationId,
  setApplicationId,
  clientId,
  setClientId,
  clientSecret,
  setClientSecret,
  onSave,
  saving,
  isConfigured,
  configuredApplicationId,
  configuredClientId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId: string;
  setApplicationId: (v: string) => void;
  clientId: string;
  setClientId: (v: string) => void;
  clientSecret: string;
  setClientSecret: (v: string) => void;
  onSave: () => void;
  saving: boolean;
  isConfigured: boolean;
  configuredApplicationId: string;
  configuredClientId: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant={isConfigured ? 'outline' : 'default'}>
          <KeyRound className="h-4 w-4" />
          {isConfigured ? 'Reconfigure API credentials' : 'Configure API credentials'}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Amazon Business Reporting API credentials</DialogTitle>
          <DialogDescription>
            Register a Solution Provider app at{' '}
            <a
              href="https://docs.business.amazon.com"
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2"
            >
              docs.business.amazon.com
            </a>{' '}
            (separate from any SP-API / Seller Central app you already have)
            and copy the App ID + LWA credentials below. Atlas encrypts the
            secret before it touches disk; the App ID + Client ID are
            non-secret and stored on the integration metadata. Amazon typically
            takes 2–4 weeks to approve your app — until then, use the CSV
            upload path on the card.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="amzb-application-id">Application ID</Label>
            <Input
              id="amzb-application-id"
              value={applicationId}
              onChange={(e) => setApplicationId(e.target.value)}
              placeholder={
                isConfigured
                  ? `Current: ${configuredApplicationId.slice(0, 28)}…`
                  : 'amzn1.application.…'
              }
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="amzb-client-id">LWA Client ID</Label>
            <Input
              id="amzb-client-id"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={
                isConfigured
                  ? `Current: ${configuredClientId.slice(0, 32)}…`
                  : 'amzn1.application-oa2-client.…'
              }
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="amzb-client-secret">LWA Client secret</Label>
            <Input
              id="amzb-client-secret"
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={isConfigured ? '••••••••••• (re-enter to rotate)' : 'amzn1.oa2-cs.v1.…'}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={
              saving ||
              !applicationId.trim() ||
              !clientId.trim() ||
              !clientSecret.trim()
            }
          >
            {saving ? 'Saving…' : 'Save credentials'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
